import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import PropTypes from "prop-types";

import { formatCallingFrom } from "../helper/patientFormatters";
import PatientCommunicationService from "../service/PatientCommunicationService";
import AiTranscriptModal from "../sections/aiTranscript/AiTranscriptModal";
import CallErrorDialog from "./CallErrorDialog";
import CallFloater from "./CallFloater";

/**
 * ActiveCallProvider — the app-wide owner of "is there a call in progress
 * right now, and should the transcript modal be visible?".
 *
 * Why it exists: the AI-transcript call runs entirely in-browser (LiveKit
 * WebRTC room, published mic track, subscribed remote audio, transcript
 * poll). All of that used to live inside AiTranscriptModal, whose only
 * mount point was inside PatientDetails. That meant closing the dialog OR
 * navigating anywhere else tore the room down and posted /end. The care
 * manager could never look up a second piece of info during a call.
 *
 * Fix: mount the modal ONCE at app root, above the Router. Provider owns
 * whether the modal renders its overlay (`modalVisible`) and whether a
 * call is active at all (`activeCall != null`). While `activeCall` is
 * truthy, the modal stays mounted regardless of route or overlay
 * visibility, so its LiveKit room survives navigation and minimise.
 *
 * The modal itself continues to own the state machine, the room, the
 * mic, the poll — it just no longer decides when to end the call as a
 * side effect of being hidden. Two paths end a call now:
 *   1. Explicit end (End button in modal header, floater hangup)
 *   2. `dismiss()` from the provider (unmounts the modal, whose cleanup
 *      posts /end idempotently). Reached from S0/S1 X-click (nothing to
 *      keep alive) and from S5 Done (review completed).
 *
 * Minimise vs dismiss decision lives in `handleModalClose` below. During
 * a connected S2 call the X becomes a minimise; every other step it's a
 * dismiss.
 *
 * The provider also renders CallFloater below the modal — a persistent
 * bottom-center pill visible while a call is up and the modal is hidden.
 *
 * Broadcast callbacks (onSessionChange / onCallStateChange / onStepChange
 * / onInitErrorChange) let the modal push its internal state up so the
 * floater can show patient + elapsed time and so the provider can react
 * (auto-restore on unexpected drop, auto-restore into review on end).
 */
const ActiveCallContext = createContext(null);

/** Read the provider from a consumer. Throws if nobody mounted one. */
export const useActiveCall = () => {
  const ctx = useContext(ActiveCallContext);
  if (!ctx) {
    throw new Error("useActiveCall must be used inside <ActiveCallProvider>");
  }
  return ctx;
};

/**
 * Non-throwing variant — returns the context value or null when no
 * provider is mounted. Use from shared code (useSheetStack, tests,
 * storybooks) that may run outside the Patient shell without wanting
 * to crash on a missing provider.
 */
export const useActiveCallMaybe = () => useContext(ActiveCallContext);

/**
 * ZY-10356 — one place that answers "is the transcript modal visible
 * right now?" so every shell editor can gate its own event listeners
 * identically. Returns both the live boolean (for render-time gates)
 * and a ref (for event handlers captured once in a subscribe effect,
 * which need the current value at fire time, not at mount time).
 *
 * Used by useSheetStack (covers the big-nine + Drug schedule) and by
 * AnthropometricEditor's direct ANTH_V2_SELECT_PARAM listener. Any
 * future editor that subscribes to a section event bus should read
 * through this helper rather than consuming the context inline, so
 * the gate rule stays in one file.
 *
 * Returns { gated: false, gatedRef: {current:false} } when no provider
 * is mounted — tests and storybooks don't need to wrap the tree.
 */
export const useTranscriptModalGate = () => {
  const ctx = useContext(ActiveCallContext);
  const gated = !!(ctx && ctx.modalVisible);
  const gatedRef = useRef(gated);
  useEffect(() => { gatedRef.current = gated; }, [gated]);
  return { gated, gatedRef };
};

const ActiveCallProvider = ({ children }) => {
  /* activeCall.patient / .appointment are the SEEDS the modal opens with
     (identity in the header, patientId in every API call). seededSession
     is set for the aspire path where useCallFlow's /initiate-call already
     provisioned a LiveKit session; the modal reads it and skips its own
     /initiate. Everything else on activeCall (session, callState, step,
     startedAt, initError) is populated by callbacks from the modal as it
     progresses through the state machine. */
  const [activeCall, setActiveCall] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  /* The modal exposes its endCall + cancelCall via useImperativeHandle
     so the floater's hangup can drive them without the provider having
     to duplicate the room-teardown logic. */
  const modalHandleRef = useRef(null);

  /* Portalled "Call failed" dialog for /initiate-call failures from
     INSIDE the modal (ECHO entry point). The dashboard entry point
     already pops the same CallErrorDialog via useCallFlow; this
     mirrors that handling so a transcript-mode call that can't be
     placed (e.g. "no doctor mapping exists for doctorId: 1029") lands
     on a visible modal instead of a silent `initError` the modal
     doesn't read back. */
  const [callErrorDialog, setCallErrorDialog] = useState(null);

  const patientCommService = useRef(new PatientCommunicationService()).current;

  /* Latest values as refs so callbacks below can read them without
     entering the deps of every consumer. */
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;
  const modalVisibleRef = useRef(modalVisible);
  modalVisibleRef.current = modalVisible;

  /* --- actions ------------------------------------------------------ */

  /* Open the modal on top of an existing patient context. Two entry
     points hit this: the sidebar's "ECHO - AI transcript" row and the
     header's Call patient click BEFORE /initiate-call fires. If a call
     is already up, we RESTORE it (floater semantics — clicking ECHO
     while a call is running always brings you back to the same call).
     Otherwise we open the modal fresh with patient/appointment seeded. */
  const openCall = useCallback((patient, appointment) => {
    if (activeCallRef.current) {
      setModalVisible(true);
      return;
    }
    setActiveCall({
      patient,
      appointment,
      seededSession: null,
      session: null,
      callState: "",
      step: 0,
      startedAt: 0,
      initError: "",
    });
    setModalVisible(true);
  }, []);

  /* Aspire path from useCallFlow: /initiate-call responded vendor=aspire
     with a provisioned LiveKit session. Modal opens straight into S1 and
     skips its own /initiate. If a call is already up, treat as restore
     (defensive — the call button should be disabled). */
  const startAspireCall = useCallback(({ patient, appointment, seededSession }) => {
    if (activeCallRef.current) {
      setModalVisible(true);
      return;
    }
    setActiveCall({
      patient,
      appointment,
      seededSession,
      session: null,
      callState: "",
      step: 0,
      startedAt: 0,
      initError: "",
    });
    setModalVisible(true);
  }, []);

  /* Hide the modal but keep the call alive. Requires an active call in
     the connected S2 state — anything else is a bug in the caller and we
     no-op. */
  const minimise = useCallback(() => {
    const a = activeCallRef.current;
    if (!a || a.step !== 2) return;
    setModalVisible(false);
  }, []);

  /** Show the modal again for the current activeCall. */
  const restore = useCallback(() => {
    if (!activeCallRef.current) return;
    setModalVisible(true);
  }, []);

  /** Full teardown — unmounts the modal, which posts /end from its own
   *  cleanup. Safe to call in any state; a no-op if no call is active. */
  const dismiss = useCallback(() => {
    setModalVisible(false);
    setActiveCall(null);
  }, []);

  /* Floater hangup — end the call but KEEP the modal path alive so the
     care manager lands on the review flow. Restore the modal so they can
     see it. The modal's own endCall (invoked via imperative handle)
     transitions step 2 → 3 which we pick up in onStepChange below. */
  const requestEndCall = useCallback(() => {
    if (!activeCallRef.current) return;
    setModalVisible(true);
    const h = modalHandleRef.current;
    if (h && typeof h.endCall === "function") h.endCall();
  }, []);

  /* --- state broadcast plumbing (modal → provider) ------------------ */

  const patchActiveCall = useCallback((patch) => {
    setActiveCall((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const onSessionChange = useCallback((session) => {
    patchActiveCall({ session });
  }, [patchActiveCall]);

  /* Stamp startedAt the first time callState flips to "connected" so the
     floater timer runs from patient-answer, not from initiate. Also keep
     callState on activeCall so the floater visibility gate can read it. */
  const onCallStateChange = useCallback((callState) => {
    setActiveCall((prev) => {
      if (!prev) return prev;
      const startedAt = prev.startedAt || (callState === "connected" ? Date.now() : 0);
      return { ...prev, callState, startedAt };
    });
  }, []);

  const onStepChange = useCallback((step) => {
    setActiveCall((prev) => (prev ? { ...prev, step } : prev));
    /* Any transition into or beyond review (step 3+) means the call is
       over. If we were minimised, bring the modal back so the CM sees
       the review flow rather than a stale floater. Floater's own
       visibility gate (step === 2) will drop it on its own. */
    if (step >= 3 && !modalVisibleRef.current) setModalVisible(true);
  }, []);

  const onInitErrorChange = useCallback((initError) => {
    patchActiveCall({ initError });
  }, [patchActiveCall]);

  /* The modal's X button unconditionally calls onClose. Provider decides
     minimise vs dismiss based on whether the call is genuinely running.
     Minimise whenever a LiveKit room is up — any non-idle, non-terminal
     callState — regardless of step: the back-arrow (ZY-10356) can leave
     the reviewer on step 0 while the call keeps running, and hitting X
     there must preserve the call, not tear it down. Dismiss only when
     there's nothing live to minimise. */
  const handleModalClose = useCallback(() => {
    const a = activeCallRef.current;
    const isLive = !!a && !!a.callState && a.callState !== "dropped";
    if (isLive) {
      setModalVisible(false);
      return;
    }
    dismiss();
  }, [dismiss]);

  const registerHandle = useCallback((handle) => {
    modalHandleRef.current = handle;
  }, []);

  /* The modal's own "Call patient" button (S0, ECHO-menu open path) hands
     the click up here — same /initiate-call the useCallFlow adapter posts,
     so the vendor branching stays in one place. On aspire the reply
     re-seeds the modal's LiveKit session; on sparktg (shouldn't happen
     from this entry point — ECHO is hidden for non-aspire categories)
     the modal is closed and the caller-side confirm dialog would be
     unreachable, so we surface it inline via initError.

     `target` is `{countryCode, phoneNo}` — the number the modal picked. */
  const handleCallRequest = useCallback((target, callbacks) => {
    // Optional callbacks let the modal reset its own "Connecting…"
    // button state when the request fails (otherwise the modal would
    // stay in the initiating UI forever once the error dialog is
    // dismissed). Success path doesn't need a hook — the modal's
    // seededSession-watching effect already clears initiating when
    // the aspire payload lands.
    const onError = callbacks && typeof callbacks.onError === "function"
      ? callbacks.onError
      : null;
    const a = activeCallRef.current;
    if (!a) {
      if (onError) onError();
      return;
    }
    const patient = a.patient || {};
    const appointmentId = (a.appointment && a.appointment.id) || 0;
    if (!appointmentId || !patient.id) {
      const msg = "Appointment or patient missing — reopen from the header.";
      patchActiveCall({ initError: msg });
      setCallErrorDialog({ title: "Call failed", message: msg });
      if (onError) onError();
      return;
    }
    patientCommService.initiateCall(
      {
        patientId:          patient.id,
        patientCountryCode: String((target && target.countryCode) || ""),
        patientPhoneNo:     String((target && target.phoneNo) || ""),
        appointmentId:      Number(appointmentId),
      },
      (res) => {
        const vendor = res && res.vendor;
        const callingFrom = formatCallingFrom(
          res && res.callerCountryCode,
          res && res.callerPhoneNo,
        );
        if (vendor === "aspire") {
          const aspire = res.aspire || {};
          patchActiveCall({
            seededSession: {
              transcriptId:   aspire.transcriptId,
              callSid:        aspire.callSid,
              callingFrom,
              mode:           aspire.mode,
              livekitUrl:     aspire.livekitUrl,
              livekitRoom:    aspire.livekitRoom,
              livekitToken:   aspire.livekitToken,
              tokenExpiresAt: aspire.tokenExpiresAt,
            },
            initError: "",
          });
          return;
        }
        /* SparkTG from inside the modal is a defensive branch — ECHO
           is hidden for non-nutritionist/physio categories, and the
           header-level useCallFlow.callNumber handles vendor branching
           for aspire-eligible users. If we get here anyway, surface a
           readable error rather than silently ending. */
        const wrongFlowMsg =
          "This patient is on the phone-only calling flow. Use Call from the header.";
        patchActiveCall({ initError: wrongFlowMsg });
        setCallErrorDialog({ title: "Call failed", message: wrongFlowMsg });
        if (onError) onError();
      },
      (err) => {
        const msg = (err && err.message) || "Unable to initiate the call. Please try again.";
        patchActiveCall({ initError: msg });
        setCallErrorDialog({ title: "Call failed", message: msg });
        if (onError) onError(err);
      },
    );
  }, [patchActiveCall, patientCommService]);

  /* --- beforeunload guard ------------------------------------------- */
  useEffect(() => {
    if (!activeCall) return undefined;
    const onBeforeUnload = (e) => {
      /* Modern browsers ignore custom text and show their own generic
         warning, but returnValue is still required to trigger it. */
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [activeCall]);

  /* --- exported API ------------------------------------------------- */

  /* `callActive` is the flag every Call button on the app reads to
     disable itself while a call is up. Callable during S1 (ringing) too
     — you can't start a second call while the first is still trying to
     connect either. Also covers the back-arrow case (ZY-10356): step
     may be 0 (list view) while a LiveKit room is still up, so OR in a
     callState check — any live state counts regardless of step. */
  const callActive = !!activeCall && (
    (activeCall.step >= 1 && activeCall.step <= 2) ||
    (!!activeCall.callState && activeCall.callState !== "dropped")
  );

  const value = useMemo(() => ({
    activeCall,
    modalVisible,
    callActive,
    openCall,
    startAspireCall,
    minimise,
    restore,
    dismiss,
    requestEndCall,
  }), [activeCall, modalVisible, callActive, openCall, startAspireCall, minimise, restore, dismiss, requestEndCall]);

  /* Floater visibility: call is connected AND the modal is hidden.
     Any other combination either has nothing to show or is already
     showing the full modal UI. Step is no longer the gate (ZY-10356
     back-arrow lands on step 0 while the call keeps running); the
     connected callState alone is the right signal. */
  const floaterVisible = !!activeCall
    && activeCall.callState === "connected"
    && !modalVisible;

  return (
    <ActiveCallContext.Provider value={value}>
      {children}
      {activeCall ? (
        <AiTranscriptModal
          open={modalVisible}
          onClose={handleModalClose}
          patient={activeCall.patient}
          appointment={activeCall.appointment}
          seededSession={activeCall.seededSession}
          onSessionChange={onSessionChange}
          onCallStateChange={onCallStateChange}
          onStepChange={onStepChange}
          onInitErrorChange={onInitErrorChange}
          registerHandle={registerHandle}
          /* Modal's own Call patient button routes through here so both
             entry points (header useCallFlow / modal internal) fire the
             same /initiate-call and get the same vendor branching. */
          onCallRequest={handleCallRequest}
        />
      ) : null}
      {floaterVisible ? (
        <CallFloater
          patientName={(activeCall.patient && activeCall.patient.name) || ""}
          startedAt={activeCall.startedAt}
          onRestore={restore}
          onHangup={requestEndCall}
        />
      ) : null}
      {callErrorDialog ? (
        <CallErrorDialog
          title={callErrorDialog.title}
          message={callErrorDialog.message}
          onClose={() => setCallErrorDialog(null)}
        />
      ) : null}
    </ActiveCallContext.Provider>
  );
};

ActiveCallProvider.propTypes = {
  children: PropTypes.node,
};

export default ActiveCallProvider;
